/**
 * Business Analyzer - Parses natural language business descriptions
 * and extracts structured business information.
 * Browser-compatible version.
 */

import type { ROIDiagnosis, ROIRecommendation, EfficiencyMetric } from './types';
import { generateId } from './id';

export interface BusinessAnalysis {
  businessName: string;
  businessType: BusinessType;
  industry: string;
  size: 'solo' | 'small' | 'medium';
  services: string[];
  targetCustomers: string[];
  challenges: string[];
  goals: string[];
  suggestedModules: ModuleRecommendation[];
}

export type BusinessType =
  | 'retail'
  | 'service'
  | 'consulting'
  | 'restaurant'
  | 'freelance'
  | 'agency'
  | 'ecommerce'
  | 'manufacturing'
  | 'other';

export interface ModuleRecommendation {
  name: string;
  reason: string;
  priority: 'essential' | 'recommended' | 'optional';
}

const BUSINESS_TYPE_PATTERNS: [BusinessType, RegExp[]][] = [
  ['restaurant', [/음식점|식당|카페|레스토랑|베이커리|커피|restaurant|cafe|bakery/i]],
  ['freelance', [/프리랜서|1인 사업|freelance|solo/i]],
  ['consulting', [/컨설팅|상담|자문|코칭|consulting|advisor/i]],
  ['agency', [/에이전시|대행|마케팅|광고|agency|marketing/i]],
  ['ecommerce', [/온라인|이커머스|인터넷|쇼핑몰|ecommerce|online/i]],
  ['manufacturing', [/제조|공장|생산|manufacturing|factory/i]],
  ['retail', [/가게|상점|샵|매장|판매|소매|shop|store|retail/i]],
  ['service', [/서비스|정비|수리|청소|배달|service/i]],
  ['other', []],
];

const INDUSTRY_KEYWORDS = [
  { pattern: /it|개발|소프트웨어|앱|tech|software|app/i, industry: 'IT/기술' },
  { pattern: /디자인|디자이너|design|designer|creative/i, industry: '디자인' },
  { pattern: /교육|학원|강의|education|teaching/i, industry: '교육' },
  { pattern: /건강|헬스|피트니스|요가|health|fitness/i, industry: '헬스케어' },
  { pattern: /뷰티|미용|네일|헤어|beauty|salon/i, industry: '뷰티' },
  { pattern: /음식|요리|food|culinary/i, industry: '식품/요식' },
  { pattern: /패션|의류|fashion|clothing/i, industry: '패션' },
  { pattern: /부동산|real estate|property/i, industry: '부동산' },
  { pattern: /법률|변호사|legal|lawyer/i, industry: '법률' },
  { pattern: /회계|세무|accounting|tax/i, industry: '회계/세무' },
];

const CHALLENGE_PATTERNS = [
  { pattern: /고객.*관리|customer.*management/i, challenge: '고객 관리 체계화' },
  { pattern: /일정|스케줄|schedule/i, challenge: '일정 관리' },
  { pattern: /매출|수익|revenue|sales/i, challenge: '매출 증대' },
  { pattern: /마케팅|홍보|marketing|promotion/i, challenge: '마케팅/홍보' },
  { pattern: /직원|인력|staff|employee/i, challenge: '인력 관리' },
  { pattern: /재고|inventory/i, challenge: '재고 관리' },
  { pattern: /자동화|효율|automation|efficiency/i, challenge: '업무 자동화' },
];

export function analyzeBusinessDescription(description: string): BusinessAnalysis {
  const nameMatch = description.match(/["']([^"']+)["']/) ||
                    description.match(/^([가-힣a-zA-Z0-9\s]+?)(을|를|은|는|이|가|에서)/);
  const businessName = nameMatch ? nameMatch[1].trim() : '내 비즈니스';

  let businessType: BusinessType = 'other';
  for (const [type, patterns] of BUSINESS_TYPE_PATTERNS) {
    if (patterns.some(p => p.test(description))) {
      businessType = type;
      break;
    }
  }

  let industry = '일반';
  for (const { pattern, industry: ind } of INDUSTRY_KEYWORDS) {
    if (pattern.test(description)) {
      industry = ind;
      break;
    }
  }

  let size: 'solo' | 'small' | 'medium' = 'small';
  if (/1인|혼자|solo|프리랜서|freelance/i.test(description)) {
    size = 'solo';
  } else if (/팀|직원|staff|employee|명/i.test(description)) {
    const numMatch = description.match(/(\d+)\s*명/);
    if (numMatch) {
      const count = parseInt(numMatch[1], 10);
      size = count > 10 ? 'medium' : 'small';
    }
  }

  const services: string[] = [];
  const serviceMatches = description.match(/(?:제공|하는|합니다|서비스)[^,.]*[가-힣a-zA-Z]+/g);
  if (serviceMatches) {
    services.push(...serviceMatches.map(s => s.replace(/제공|하는|합니다|서비스/g, '').trim()).filter(Boolean));
  }
  if (services.length === 0) {
    services.push('일반 서비스');
  }

  const targetCustomers: string[] = [];
  if (/b2b|기업|회사|business/i.test(description)) {
    targetCustomers.push('기업 고객');
  }
  if (/b2c|개인|소비자|consumer|일반/i.test(description)) {
    targetCustomers.push('개인 고객');
  }
  if (/소상공인|자영업|small business/i.test(description)) {
    targetCustomers.push('소상공인');
  }
  if (targetCustomers.length === 0) {
    targetCustomers.push('일반 고객');
  }

  const challenges: string[] = [];
  for (const { pattern, challenge } of CHALLENGE_PATTERNS) {
    if (pattern.test(description)) {
      challenges.push(challenge);
    }
  }
  if (challenges.length === 0) {
    challenges.push('업무 효율화');
  }

  const goals: string[] = [];
  if (/성장|확장|growth|expand/i.test(description)) {
    goals.push('사업 성장');
  }
  if (/효율|자동화|automation|efficiency/i.test(description)) {
    goals.push('업무 효율화');
  }
  if (/고객.*늘|고객.*확보|customer.*acqui/i.test(description)) {
    goals.push('신규 고객 확보');
  }
  if (/매출|수익|revenue|profit/i.test(description)) {
    goals.push('매출 증대');
  }
  if (goals.length === 0) {
    goals.push('업무 체계화', '성장 기반 마련');
  }

  const suggestedModules = generateModuleRecommendations(businessType, challenges, goals);

  return {
    businessName,
    businessType,
    industry,
    size,
    services,
    targetCustomers,
    challenges,
    goals,
    suggestedModules,
  };
}

function generateModuleRecommendations(
  businessType: BusinessType,
  challenges: string[],
  goals: string[]
): ModuleRecommendation[] {
  const recommendations: ModuleRecommendation[] = [];

  recommendations.push({
    name: 'tasks',
    reason: '할 일과 프로젝트를 체계적으로 관리',
    priority: 'essential',
  });

  recommendations.push({
    name: 'notes',
    reason: '비즈니스 아이디어와 회의록을 기록',
    priority: 'essential',
  });

  if (['service', 'consulting', 'agency', 'retail', 'ecommerce'].includes(businessType) ||
      challenges.includes('고객 관리 체계화')) {
    recommendations.push({
      name: 'crm',
      reason: '고객 관계를 체계적으로 관리하고 영업 기회를 추적',
      priority: 'essential',
    });
  } else {
    recommendations.push({
      name: 'crm',
      reason: '연락처와 거래처 정보를 관리',
      priority: 'recommended',
    });
  }

  if (goals.includes('매출 증대') || goals.includes('사업 성장')) {
    recommendations.push({
      name: 'insights',
      reason: '비즈니스 현황을 한눈에 파악하고 개선점을 발견',
      priority: 'essential',
    });
  } else {
    recommendations.push({
      name: 'insights',
      reason: '업무 현황 요약과 분석',
      priority: 'recommended',
    });
  }

  recommendations.push({
    name: 'assistant',
    reason: 'AI 기반 업무 제안과 자동화',
    priority: 'recommended',
  });

  return recommendations;
}

export function generateWelcomeMessage(analysis: BusinessAnalysis): string {
  const typeNames: Record<BusinessType, string> = {
    retail: '소매업',
    service: '서비스업',
    consulting: '컨설팅',
    restaurant: '요식업',
    freelance: '프리랜서',
    agency: '에이전시',
    ecommerce: '이커머스',
    manufacturing: '제조업',
    other: '비즈니스',
  };

  return `환영합니다! "${analysis.businessName}"의 ${analysis.industry} ${typeNames[analysis.businessType]}을 위한 워크스페이스를 준비했습니다.`;
}

/**
 * Generate ROI/Efficiency Diagnosis based on business analysis
 */
export function generateROIDiagnosis(
  workspaceId: string,
  analysis: BusinessAnalysis
): ROIDiagnosis {
  // Base scores based on business size and challenges
  const sizeMultiplier = analysis.size === 'solo' ? 0.7 : analysis.size === 'small' ? 1.0 : 1.3;
  const challengeCount = analysis.challenges.length;

  // Time efficiency calculation
  const baseTimeScore = 45 + Math.floor(Math.random() * 15);
  const timeImprovement = 25 + Math.floor(challengeCount * 8);
  const timeEfficiency: EfficiencyMetric = {
    label: '시간 효율성',
    currentScore: baseTimeScore,
    potentialScore: Math.min(95, baseTimeScore + timeImprovement),
    improvement: timeImprovement,
    description: getTimeEfficiencyDescription(analysis.challenges),
  };

  // Cost efficiency calculation
  const baseCostScore = 50 + Math.floor(Math.random() * 10);
  const costImprovement = 20 + Math.floor(challengeCount * 6);
  const costEfficiency: EfficiencyMetric = {
    label: '비용 효율성',
    currentScore: baseCostScore,
    potentialScore: Math.min(92, baseCostScore + costImprovement),
    improvement: costImprovement,
    description: getCostEfficiencyDescription(analysis.businessType),
  };

  // Automation potential calculation
  const automationBaseScore = 30 + Math.floor(Math.random() * 20);
  const automationImprovement = 35 + Math.floor(sizeMultiplier * 10);
  const automationPotential: EfficiencyMetric = {
    label: '자동화 잠재력',
    currentScore: automationBaseScore,
    potentialScore: Math.min(90, automationBaseScore + automationImprovement),
    improvement: automationImprovement,
    description: getAutomationDescription(analysis.goals),
  };

  // Overall score (weighted average)
  const overallScore = Math.round(
    (timeEfficiency.currentScore * 0.35 +
      costEfficiency.currentScore * 0.3 +
      automationPotential.currentScore * 0.35)
  );

  // Estimated savings calculations
  const baseHourlySavings = analysis.size === 'solo' ? 15 : analysis.size === 'small' ? 30 : 60;
  const estimatedTimeSavedHours = Math.round(baseHourlySavings * sizeMultiplier * (1 + challengeCount * 0.15));
  const hourlyRate = 30000; // 30,000 KRW per hour average
  const estimatedMonthlySavings = estimatedTimeSavedHours * hourlyRate;

  // Generate recommendations
  const recommendations = generateROIRecommendations(analysis, {
    timeImprovement,
    costImprovement,
    automationImprovement,
    estimatedTimeSavedHours,
  });

  return {
    workspaceId,
    createdAt: new Date(),
    timeEfficiency,
    costEfficiency,
    automationPotential,
    overallScore,
    estimatedMonthlySavings,
    estimatedTimeSavedHours,
    recommendations,
  };
}

function getTimeEfficiencyDescription(challenges: string[]): string {
  if (challenges.includes('고객 관리 체계화')) {
    return '고객 관리 자동화로 주당 5-10시간 절약 가능';
  }
  if (challenges.includes('일정 관리')) {
    return '스케줄 자동화로 주당 3-6시간 절약 가능';
  }
  if (challenges.includes('업무 자동화')) {
    return '반복 업무 자동화로 주당 8-15시간 절약 가능';
  }
  return '체계적인 워크플로우로 주당 4-8시간 절약 가능';
}

function getCostEfficiencyDescription(businessType: BusinessType): string {
  const descriptions: Record<BusinessType, string> = {
    retail: '재고 최적화 및 고객 이탈 방지로 비용 절감',
    service: '서비스 효율화 및 고객 유지율 향상',
    consulting: '프로젝트 관리 효율화로 수익성 개선',
    restaurant: '재료 관리 및 인력 최적화',
    freelance: '프로젝트 추적으로 청구 누락 방지',
    agency: '캠페인 ROI 추적 및 리소스 최적화',
    ecommerce: '재고 관리 및 고객 LTV 향상',
    manufacturing: '생산 효율성 및 품질 관리 개선',
    other: '비즈니스 프로세스 최적화로 비용 절감',
  };
  return descriptions[businessType];
}

function getAutomationDescription(goals: string[]): string {
  if (goals.includes('업무 효율화')) {
    return '반복 작업의 80% 이상 자동화 가능';
  }
  if (goals.includes('사업 성장')) {
    return '성장에 집중할 수 있도록 운영 자동화';
  }
  return 'AI 기반 업무 자동화로 생산성 향상';
}

function generateROIRecommendations(
  analysis: BusinessAnalysis,
  metrics: {
    timeImprovement: number;
    costImprovement: number;
    automationImprovement: number;
    estimatedTimeSavedHours: number;
  }
): ROIRecommendation[] {
  const recommendations: ROIRecommendation[] = [];

  // Always add task management recommendation
  recommendations.push({
    id: generateId('rec'),
    title: '태스크 파이프라인 도입',
    description: 'Linear 스타일의 워크플로우로 업무 진행 상태를 명확히 파악하고 병목 구간을 제거하세요.',
    impact: 'high',
    category: 'time',
    estimatedTimeHours: Math.round(metrics.estimatedTimeSavedHours * 0.3),
  });

  // CRM-based recommendation
  if (analysis.challenges.includes('고객 관리 체계화') ||
      ['service', 'consulting', 'agency', 'retail'].includes(analysis.businessType)) {
    recommendations.push({
      id: generateId('rec'),
      title: 'CRM 파이프라인 활용',
      description: '잠재 고객부터 충성 고객까지 체계적으로 관리하여 전환율을 높이세요.',
      impact: 'high',
      category: 'growth',
      estimatedSavings: 500000, // 50만원
    });
  }

  // Automation recommendation
  if (analysis.goals.includes('업무 효율화') || metrics.automationImprovement > 30) {
    recommendations.push({
      id: generateId('rec'),
      title: 'AI 기반 자동화',
      description: 'AI 어시스턴트가 반복 업무를 자동화하고 인사이트를 제공합니다.',
      impact: 'medium',
      category: 'automation',
      estimatedTimeHours: Math.round(metrics.estimatedTimeSavedHours * 0.4),
    });
  }

  // Analytics recommendation for growth-focused businesses
  if (analysis.goals.includes('매출 증대') || analysis.goals.includes('사업 성장')) {
    recommendations.push({
      id: generateId('rec'),
      title: '실시간 인사이트 분석',
      description: 'KPI 대시보드로 비즈니스 현황을 실시간 파악하고 기회를 포착하세요.',
      impact: 'high',
      category: 'growth',
      estimatedSavings: 800000, // 80만원
    });
  }

  // Documentation recommendation
  recommendations.push({
    id: generateId('rec'),
    title: '지식 베이스 구축',
    description: '노션 스타일의 문서화로 팀 지식을 체계적으로 관리하고 온보딩을 가속화하세요.',
    impact: 'medium',
    category: 'time',
    estimatedTimeHours: Math.round(metrics.estimatedTimeSavedHours * 0.15),
  });

  // Size-specific recommendation
  if (analysis.size === 'solo') {
    recommendations.push({
      id: generateId('rec'),
      title: '1인 기업 최적화',
      description: '모든 업무를 한 곳에서 관리하여 툴 전환 시간을 없애세요.',
      impact: 'high',
      category: 'cost',
      estimatedSavings: 200000, // 20만원 (툴 비용 절감)
    });
  } else if (analysis.size === 'medium') {
    recommendations.push({
      id: generateId('rec'),
      title: '팀 협업 최적화',
      description: '팀원 간 업무 배분과 진행 상황 공유를 효율화하세요.',
      impact: 'high',
      category: 'time',
      estimatedTimeHours: Math.round(metrics.estimatedTimeSavedHours * 0.25),
    });
  }

  return recommendations.slice(0, 5); // Max 5 recommendations
}
